import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import {
  Box,
  Typography,
  Container,
  CircularProgress,
  Alert,
  Dialog,
  DialogContent
} from '@mui/material'
import RealtorCard, { type RealtorData } from '../components/RealtorCard'
import RealtorDetail from '../components/RealtorDetail'
import Pagination from '../components/Pagination'
import { useContentByEmbed } from '../hooks/useContentByEmbed'
import { useInfiniteContentByEmbed } from '../hooks/useInfiniteContentByEmbed'
// Importar imagen por defecto para agentes
// Sube tu imagen a src/assets/ con el nombre agent-default.png (o cambia el nombre aquí)
// Servida desde el CDN en vez de importada: en modo `lib` vite inlinea todos los assets
// sin importar assetsInlineLimit, y este PNG agregaba 275KB en base64 al bundle de
// TODOS los widgets, aunque solo lo use este y solo como ultimo fallback.
const agentDefaultImage = 'https://widgets.qorus.io/widget-forge/widget-bundle/assets/agent-default.png'

export interface RealtorWidgetProps {
  embedId: string
  title?: string
  subtitle?: string
  itemsPerPage?: number
  dataConfig?: any
  onLearnMore?: (realtor: RealtorData) => void
  centerItems?: boolean // Bandera para centrar los items cuando hay pocos realtors
}


const RealtorWidget: React.FC<RealtorWidgetProps> = ({
  embedId,
  title = 'Our Realtors',
  subtitle,
  itemsPerPage: itemsPerPageProp = 12,
  dataConfig,
  onLearnMore,
  centerItems = false
}) => {
  // Extraer valores primitivos de forma estable usando useState
  const [configValues, setConfigValues] = useState<{
    pageSize: number
    paginationMode: string
    sortField?: string
    sortDir?: string
  }>({
    pageSize: itemsPerPageProp,
    paginationMode: 'scroll'
  })

  // Actualizar valores solo cuando realmente cambien los valores primitivos
  useEffect(() => {
    const listDefaults = dataConfig?.config?.ui?.listDefaults
    const newPageSize = listDefaults?.pageSize || itemsPerPageProp
    const newPaginationMode = listDefaults?.paginationMode || 'scroll'
    const sortString = listDefaults?.sort

    let newSortField: string | undefined
    let newSortDir: string | undefined

    if (sortString && typeof sortString === 'string') {
      const parts = sortString.split(':')
      newSortField = parts[0]
      newSortDir = parts.length === 2 ? parts[1] : undefined
    }

    setConfigValues(prev => {
      // Solo actualizar si los valores realmente cambiaron
      if (
        prev.pageSize !== newPageSize ||
        prev.paginationMode !== newPaginationMode ||
        prev.sortField !== newSortField ||
        prev.sortDir !== newSortDir
      ) {
        return {
          pageSize: newPageSize,
          paginationMode: newPaginationMode,
          sortField: newSortField,
          sortDir: newSortDir
        }
      }
      return prev
    })
  }, [
    dataConfig?.config?.ui?.listDefaults?.pageSize,
    dataConfig?.config?.ui?.listDefaults?.paginationMode,
    dataConfig?.config?.ui?.listDefaults?.sort,
    itemsPerPageProp
  ])

  const pageSize = configValues.pageSize
  const paginationMode = configValues.paginationMode
  const useInfiniteScroll = paginationMode === 'scroll'
  const sortField = configValues.sortField
  const sortDir = configValues.sortDir

  const [currentPage, setCurrentPage] = useState(1)
  const [selectedRealtor, setSelectedRealtor] = useState<RealtorData | null>(null)
  const loadMoreRef = useRef<HTMLDivElement>(null)

  // Construir filtros base (sin page/pageSize para infinite query)
  const baseFilters = useMemo(() => {
    const filters: any = {}
    
    // Agregar sort y dir si están configurados
    if (sortField) {
      filters.sort = sortField
    }
    if (sortDir) {
      filters.dir = sortDir
    }
    
    return filters
  }, [sortField, sortDir])

  // Usar useInfiniteQuery para scroll infinito, useQuery para paginación normal
  const infiniteQuery = useInfiniteContentByEmbed(
    embedId,
    undefined,
    baseFilters,
    pageSize
  )

  const regularQuery = useContentByEmbed(
    embedId,
    undefined,
    {
      ...baseFilters,
      page: currentPage,
      pageSize: pageSize
    }
  )

  // Seleccionar la query apropiada según el modo
  const queryResult = useInfiniteScroll ? infiniteQuery : regularQuery
  const contentData = useInfiniteScroll 
    ? (queryResult as typeof infiniteQuery).data?.pages?.[((queryResult as typeof infiniteQuery).data?.pages?.length ?? 1) - 1]
    : (queryResult as typeof regularQuery).data
  const isLoadingContent = queryResult.isLoading
  const contentError = queryResult.error
  const isFetching = queryResult.isFetching
  const hasNextPage = useInfiniteScroll ? (queryResult as typeof infiniteQuery).hasNextPage ?? false : false
  const fetchNextPage = useInfiniteScroll ? (queryResult as typeof infiniteQuery).fetchNextPage : undefined
  const isFetchingNextPage = useInfiniteScroll ? (queryResult as typeof infiniteQuery).isFetchingNextPage ?? false : false

  useEffect(() => {
    console.log('RealtorWidget contentData', contentData)
  }, [contentData])

  // Resetear cuando cambia el modo de paginación
  useEffect(() => {
    setCurrentPage(1)
    if (useInfiniteScroll && infiniteQuery) {
      // Resetear infinite query
      (infiniteQuery as typeof infiniteQuery).refetch()
    }
  }, [useInfiniteScroll])

  // Función helper para transformar datos de API a RealtorData
  const transformToRealtorData = useCallback((dataArray: any[]): RealtorData[] => {
    if (!Array.isArray(dataArray)) return []
    
    // Obtener imagen por defecto: primero de dataConfig, luego imagen local, luego fallback
    const defaultImage = dataConfig?.config?.photoDefault || agentDefaultImage || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=300&fit=crop'
    
    return dataArray.map((item: any) => ({
      id: item.id?.toString() || '',
      // Usar imagen por defecto si photo es null, undefined, o string vacío
      image: item.data?.photo && item.data.photo !== null && item.data.photo !== '' ? item.data.photo : defaultImage,
      name: item.data?.fullName?.trim() || 'Agent Name',
      title: item.data?.role || 'REALTOR',
      description: item.data?.shortDescription || item.data?.longDescription || '',
      phone: item.data?.phone || '(555) 123-4567',
      email: item.data?.email || 'agent@example.com',
      url: item.url
    }))
  }, [dataConfig?.config?.photoDefault])

  // Obtener todos los realtors según el modo
  const currentRealtors = useMemo(() => {
    if (useInfiniteScroll) {
      // Con infinite query, acumular todos los datos de todas las páginas
      const infiniteData = (queryResult as typeof infiniteQuery).data
      if (!infiniteData?.pages) return []
      
      // Aplanar todas las páginas y transformar
      const allItems: any[] = []
      infiniteData.pages.forEach(page => {
        const pageData = page?.data?.data
        if (Array.isArray(pageData)) {
          allItems.push(...pageData)
        }
      })
      
      return transformToRealtorData(allItems)
    } else {
      // Con query normal, solo la página actual
      const pageData = contentData?.data?.data
      if (!Array.isArray(pageData)) return []
      return transformToRealtorData(pageData)
    }
  }, [useInfiniteScroll, queryResult, contentData, transformToRealtorData])

  // Obtener total de items
  const totalItems = useMemo(() => {
    if (useInfiniteScroll) {
      const infiniteData = (queryResult as typeof infiniteQuery).data
      return infiniteData?.pages?.[0]?.data?.total || 0
    } else {
      return contentData?.data?.total || 0
    }
  }, [useInfiniteScroll, queryResult, contentData])


  const handleLearnMore = (realtor: RealtorData) => {
    setSelectedRealtor(realtor)
    onLearnMore?.(realtor)
  }

  const handleCloseDetail = () => {
    setSelectedRealtor(null)
  }

  // Scroll infinito - cargar siguiente página usando fetchNextPage de React Query
  const loadMoreRealtors = useCallback(async () => {
    if (!useInfiniteScroll || !fetchNextPage || isFetchingNextPage || !hasNextPage) return
    
    try {
      await fetchNextPage()
    } catch (err) {
      console.error('Error loading more realtors:', err)
    }
  }, [useInfiniteScroll, fetchNextPage, isFetchingNextPage, hasNextPage])

  // Usar Intersection Observer para detectar cuando el final de la lista es visible (solo si paginationMode es 'scroll')
  useEffect(() => {
    if (!useInfiniteScroll) return
    
    const observerTarget = loadMoreRef.current
    if (!observerTarget || !hasNextPage || isFetchingNextPage) return

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          console.log('End of realtors list is visible, loading more...')
          loadMoreRealtors()
        }
      },
      {
        root: null, // Usar viewport como root
        rootMargin: '200px', // Cargar 200px antes de que sea visible
        threshold: 0.1
      }
    )

    observer.observe(observerTarget)

    return () => {
      if (observerTarget) {
        observer.unobserve(observerTarget)
      }
    }
  }, [useInfiniteScroll, hasNextPage, isFetchingNextPage, loadMoreRealtors])

  // Manejar cambio de p?gina (para modo paginaci?n)
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    // Scroll al inicio cuando cambia la p?gina
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Calcular total de p?ginas
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 1

  // Solo mostrar loading completo en la carga inicial (cuando no hay datos todav?a)
  // Una vez que tenemos datos, aunque isLoadingContent sea true (cargando otra p?gina),
  // no mostramos el loading completo, solo el peque?o abajo
  if (isLoadingContent && currentRealtors.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    )
  }

  if (contentError) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ fontSize: '1rem' }}>
          Error loading realtors: {contentError.message}
        </Alert>
      </Container>
    )
  }

  return (
    <Box
      sx={{
        fontFamily: 'Montserrat, sans-serif',
        padding: { xs: '30px 16px 50px 16px', md: '40px 20px 60px 20px' },
        position: 'relative'
      }}
    >
      <Container maxWidth="lg">
        {/* Realtors Grid */}
        <Box
          sx={{
            display: {
              xs: 'flex',
              sm: 'grid'
            },
            flexDirection: {
              xs: 'column',
              sm: 'unset'
            },
            alignItems: {
              xs: 'center',
              sm: centerItems && currentRealtors.length <= 2 ? 'center' : 'unset'
            },
            justifyContent: {
              xs: 'center',
              sm: centerItems && currentRealtors.length <= 2 ? 'center' : 'unset'
            },
            gridTemplateColumns: {
              sm: centerItems && currentRealtors.length <= 2 
                ? `repeat(${currentRealtors.length}, 400px)` 
                : 'repeat(2, 1fr)',
              lg: centerItems && currentRealtors.length <= 2
                ? `repeat(${currentRealtors.length}, 400px)`
                : 'repeat(3, 1fr)'
            },
            gap: { 
              xs: 2, 
              md: centerItems && currentRealtors.length <= 2 ? 2 : 3 
            },
            marginBottom: 4,
            width: {
              xs: '100%',
              sm: '100%'
            },
            '& > *': {
              width: {
                xs: '100%',
                sm: centerItems && currentRealtors.length <= 2 ? '400px' : 'auto'
              },
              maxWidth: {
                xs: '400px',
                sm: centerItems && currentRealtors.length <= 2 ? '400px' : 'none'
              }
            }
          }}
        >
          {currentRealtors.map((realtor) => (
            <RealtorCard
              key={realtor.id}
              data={realtor}
              onLearnMore={handleLearnMore}
            />
          ))}
        </Box>

        {/* Elemento sentinela para detectar el final de la lista (solo para scroll infinito) */}
        {useInfiniteScroll && (
          <Box 
            ref={loadMoreRef}
            sx={{ 
              height: '1px', 
              width: '100%',
              visibility: 'hidden'
            }}
          />
        )}

        {/* Loading indicator - mostrar abajo mientras carga (solo para scroll infinito) */}
        {useInfiniteScroll && isFetchingNextPage && (
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              py: 4,
              minHeight: 60
            }}
          >
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              Loading more realtors...
            </Typography>
          </Box>
        )}
        
        {/* Mensaje cuando no hay más páginas (solo para scroll infinito) */}
        {/*{useInfiniteScroll && !isFetchingNextPage && !hasNextPage && currentRealtors.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Showing all {totalItems} realtors
            </Typography>
          </Box>
        )}*/}

        {/* Loading indicator para modo paginación */}
        {!useInfiniteScroll && (isLoadingContent || isFetching) && currentRealtors.length > 0 && (
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              py: 4,
              minHeight: 60
            }}
          >
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              Loading page {currentPage}...
            </Typography>
          </Box>
        )}

        {/* Paginador (solo si paginationMode no es 'scroll') */}
        {!useInfiniteScroll && totalPages > 1 && (
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={pageSize}
              onPageChange={handlePageChange}
              showResultsSummary={true}
              resultsLabel="Realtors"
            />
          </Box>
        )}
      </Container>

      {/* Dialog para mostrar el detalle del realtor */}
      <Dialog
        open={!!selectedRealtor}
        onClose={handleCloseDetail}
        maxWidth="lg"
        fullWidth
    
      >
        <DialogContent sx={{ padding: 0, margin: 0 }}>
          {selectedRealtor && (
            <RealtorDetail
              data={selectedRealtor}
              onContact={(realtor) => {
                console.log('Contacting realtor:', realtor.name)
                // Aqu? puedes agregar l?gica adicional para el contacto
              }}
              onClose={handleCloseDetail}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default RealtorWidget
